import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Unlock, ShieldCheck, RefreshCcw, ArrowRight } from 'lucide-react';

const PASSWORD = '0311';

interface Props {
    onSuccess: () => void;
}

const PasswordScreen: React.FC<Props> = ({ onSuccess }) => {
    const [password, setPassword] = useState(['', '', '', '']);
    const [error, setError] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const { t } = useLanguage();

    const handleCharChange = (val: string, index: number) => {
        if (val.length > 1) val = val.slice(-1); // Only take last char
        if (!/^\d*$/.test(val)) return; // Only numbers

        const newPassword = [...password];
        newPassword[index] = val;
        setPassword(newPassword);
        setError(false);

        // Auto focus next
        if (val !== '' && index < 3) {
            setFocusedIndex(index + 1);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === 'Backspace' && password[index] === '' && index > 0) {
            setFocusedIndex(index - 1);
        }
    };

    useEffect(() => {
        if (password.every(char => char !== '')) {
            const entered = password.join('');
            setAttempts(prev => prev + 1);
            if (entered === PASSWORD) {
                setShowSuccessModal(true);
            } else {
                setError(true);
                setTimeout(() => {
                    setPassword(['', '', '', '']);
                    setFocusedIndex(0);
                    setError(false);
                }, 1000);
            }
        }
    }, [password]);

    useEffect(() => {
        if (attempts >= 1) setShowHint(true);
    }, [attempts]);

    const getHintText = () => {
        if (attempts === 1) return "Think of our special month...";
        if (attempts === 2) return "The month is Nov...";
        if (attempts >= 3) return "Nov 3rd - Our beginning! (0311)";
        return "Think about the day our story began...";
    };

    return (
        <div className="min-h-screen flex flex-col justify-center items-center px-4 bg-romantic-light dark:bg-romantic-dark py-12">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md w-full bg-white dark:bg-romantic-cardDark rounded-[2.5rem] p-8 md:p-10 shadow-2xl border border-white/20 relative overflow-hidden"
            >
                <div className="absolute top-0 left-0 w-full h-2 bg-primary-red/20 overflow-hidden">
                    <motion.div
                        className="h-full bg-primary-red"
                        animate={{ x: ['-100%', '100%'] }}
                        transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    />
                </div>

                {/* Header */}
                <div className="flex flex-col items-center mb-10">
                    <div className="w-24 h-24 rounded-3xl bg-primary-red/10 flex items-center justify-center mb-6 relative">
                        <motion.div
                            animate={error ? { x: [0, -10, 10, -10, 10, 0] } : {}}
                            className="text-primary-red"
                        >
                            <Lock size={48} />
                        </motion.div>
                        <div className="absolute -top-2 -right-2 bg-primary-red text-white text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider">Secure</div>
                    </div>

                    <h2 className="text-3xl font-playfair font-bold text-primary-red mb-3">Surprise Room</h2>
                    <p className="text-gray-600 dark:text-gray-400 text-center font-poppins">Enter the secret code to unlock your special surprise.</p>
                </div>

                {/* Input area */}
                <div className="flex flex-col items-center">
                    <div className="flex space-x-4 mb-8">
                        {password.map((char, idx) => (
                            <div key={idx} className="relative">
                                <input
                                    type="text"
                                    maxLength={1}
                                    value={char}
                                    onChange={(e) => handleCharChange(e.target.value, idx)}
                                    onKeyDown={(e) => handleKeyDown(e, idx)}
                                    autoFocus={idx === 0}
                                    className={`w-14 h-14 md:w-16 md:h-16 text-center text-3xl font-bold bg-transparent border-2 rounded-2xl outline-none transition-all ${error ? 'border-red-500 bg-red-50 text-red-500' :
                                        char !== '' ? 'border-primary-red bg-primary-red/5 text-primary-red' :
                                            focusedIndex === idx ? 'border-primary-red/50 ring-4 ring-primary-red/10' : 'border-gray-200 dark:border-gray-700 dark:text-white'
                                        }`}
                                    ref={(el) => {
                                        if (focusedIndex === idx && el) el.focus();
                                    }}
                                />
                                {focusedIndex === idx && !error && (
                                    <motion.div
                                        layoutId="focus"
                                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary-red rounded-full"
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="text-red-500 font-medium mb-6 flex items-center space-x-2"
                            >
                                <span>❌ {t('wrongPassword') || 'Incorrect code, try again'}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Action Buttons */}
                    <div className="flex space-x-4 w-full mb-8">
                        <button
                            onClick={() => { setPassword(['', '', '', '']); setFocusedIndex(0); }}
                            className="flex-1 py-4 px-6 rounded-2xl border border-gray-200 dark:border-gray-700 flex items-center justify-center space-x-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors dark:text-white"
                        >
                            <RefreshCcw size={18} />
                            <span className="font-semibold">Clear</span>
                        </button>
                        <div className="flex-1 py-4 px-6 rounded-2xl bg-primary-red text-white flex items-center justify-center space-x-2 opacity-100 shadow-lg shadow-red-200 dark:shadow-none">
                            <ShieldCheck size={18} />
                            <span className="font-semibold">Secure</span>
                        </div>
                    </div>
                </div>

                {/* Hint Section */}
                <AnimatePresence>
                    {showHint && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="border-t border-gray-100 dark:border-gray-800 pt-6 mt-2"
                        >
                            <div className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-2xl flex items-start space-x-4">
                                <div className="bg-amber-100 dark:bg-amber-800 p-2 rounded-xl text-amber-600">
                                    <Unlock size={20} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-amber-700 dark:text-amber-500 text-sm mb-1">Need a hint?</h4>
                                    <p className="text-amber-600/80 dark:text-amber-400/80 text-xs leading-5 italic">{getHintText()}</p>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Footer info */}
                <div className="mt-10 flex flex-col items-center">
                    <div className="flex space-x-2 mb-4 opacity-10 dark:opacity-30">
                        {[1, 2, 3, 4].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-400" />)}
                    </div>
                    <p className="text-[10px] text-gray-400 text-center uppercase tracking-widest leading-relaxed">Protected by love and cherished memories</p>
                </div>
            </motion.div>

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-white dark:bg-romantic-cardDark max-w-sm w-full rounded-[3rem] p-10 shadow-2xl overflow-hidden relative"
                        >
                            <div className="flex flex-col items-center text-center">
                                <motion.div
                                    initial={{ rotate: -45, scale: 0.5 }}
                                    animate={{ rotate: 0, scale: 1 }}
                                    transition={{ type: 'spring', damping: 10 }}
                                    className="w-24 h-24 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mb-8"
                                >
                                    <span className="text-5xl">🎉</span>
                                </motion.div>

                                <h3 className="text-3xl font-bold text-primary-red mb-3 font-playfair">Welcome Darling!</h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed font-poppins">Access granted. Your memories are waiting for you!</p>

                                <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-[2rem] mb-10 w-full">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">You remembered <br /><span className="font-bold text-primary-red">3rd November (0311)</span> <br />The day our miracle began ❤️</p>
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={onSuccess}
                                    className="w-full py-5 rounded-2xl bg-primary-red text-white font-bold text-lg shadow-xl shadow-red-200 dark:shadow-none flex items-center justify-center space-x-2"
                                >
                                    <span>Enter Surprise Room</span>
                                    <ArrowRight size={20} />
                                </motion.button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PasswordScreen;
